import { useEffect, useMemo, useState } from 'react'
import org_api from '../api/admin/admin_orgs'
import customer_api from '../api/customer/customer_orgs'
import api, {
	CLIENT,
	ROLE_ADMIN,
	ROLE_ORGANISATION_ADMIN,
	isRequestCancelled,
	toDateRange,
} from '../api/finvise_reports_api'
import org_admin_api from '../api/orgadmin/orgadmin_orgs'
import profapi from '../api/profile'
import valuation_api from '../api/valuation'
import {
	CancelledCard,
	CaseStatusCard,
	GeographyCard,
	KpiCard,
	LoadErrorToast,
	MonthsCard,
} from '../components/FinviseDashboardCards'
import FinviseOrgFilter from '../components/FinviseOrgFilter'
import FinviseRangePicker from '../components/FinviseRangePicker'
import {
	caseStatusSummary,
	defaultRange,
	filterRange,
	fmtDate,
	fmtDuration,
	fmtShort,
	metrics,
	ytdRange,
} from '../components/finviseMetrics'
import { filterProdOrganisations } from '../utils/prodCompanyFilter'
import '../styles/finvise-portal.css'

// colours per group, in the order the API returns them: the group colour
// for the inner ring, then one shade per reason for the outer ring
const CANCEL_PALETTE = [
	{
		color: '#0a1059',
		reasons: ['#4a5bd0', '#26318f', '#7d8be0', '#aab4ee', '#aab4ee'],
	},
	{ color: '#8f86c9', reasons: ['#b3acdc', '#d6d2ec', '#b3acdc'] },
	{
		color: '#2f4fd1',
		reasons: ['#2f4fd1', '#6d86e3', '#9aaced', '#c4cff5'],
	},
]

// groups: /cancellation_reasons payload, rows: reports in the selected range
const buildCancellations = (groups, rows) => {
	const counts = {}
	rows.forEach(r => {
		if (r.status === 'Cancelled' && r.cancellationReasonId != null)
			counts[r.cancellationReasonId] = (counts[r.cancellationReasonId] || 0) + 1
	})
	return {
		groups: groups.map((g, gi) => {
			const palette = CANCEL_PALETTE[gi % CANCEL_PALETTE.length]
			return {
				key: String(g.id),
				label: g.name,
				color: palette.color,
				reasons: (g.reasons || []).map((r, ri) => ({
					label: r.name,
					cases: counts[r.id] || 0,
					color: palette.reasons[Math.min(ri, palette.reasons.length - 1)],
				})),
			}
		}),
	}
}

// "AB" from "Acme Bank"; one-word names use their first two letters
const initials = name => {
	const words = String(name || '')
		.trim()
		.split(/\s+/)
		.filter(Boolean)
	if (!words.length) return ''
	if (words.length === 1) return words[0].slice(0, 2).toUpperCase()
	return (words[0][0] + words[1][0]).toUpperCase()
}

// wording for the picked range in sentences ("... , year to date")
const periodText = range =>
	range && range.label && range.label !== 'Custom range'
		? range.label.toLowerCase()
		: 'in the selected period'

const FinviseDashboard = ({
	orgId = null,
	role: roleProp = null,
	clientName = null,
	range: rangeProp = null,
	onRangeChange = null,
	orgFilter: orgFilterProp = null,
	onOrgFilterChange = null,
} = {}) => {
	const [data, setData] = useState([])
	const [role, setRole] = useState(roleProp)
	const [rangeState, setRangeState] = useState(defaultRange)
	// controlled when a parent lifts range state to keep Dashboard/Properties
	// on the same period, uncontrolled (own state) otherwise
	const range = rangeProp || rangeState
	const setRange = onRangeChange || setRangeState
	const [agg, setAgg] = useState(null)
	const [loading, setLoading] = useState(false)
	const [updatedAt, setUpdatedAt] = useState(null)
	const [reasonGroups, setReasonGroups] = useState([])
	const [organisations, setOrganisations] = useState([])
	// controlled when a parent lifts the company filter so it survives
	// switching between Dashboard and Report Register
	const [orgFilterState, setOrgFilterState] = useState([])
	const orgFilter = orgFilterProp || orgFilterState
	const setOrgFilter = onOrgFilterChange || setOrgFilterState
	const [resolvedOrgName, setResolvedOrgName] = useState(null)

	// main admin, viewing the cross-org console (no orgId fixed by the route)
	const showOrgFilter = role === ROLE_ADMIN && orgId == null

	// when the main admin is browsing across companies, the page title must
	// track the org filter — it should never show the single-client mock
	// name (that's only correct on a specific organisation's own page)
	const orgFilterTitle = () => {
		if (!orgFilter.length) return 'All companies'
		if (orgFilter.length === 1) {
			const org = organisations.find(o => o.id === orgFilter[0])
			return org ? org.name : 'All companies'
		}
		return orgFilter.length + ' companies'
	}
	const pageTitle = showOrgFilter
		? orgFilterTitle()
		: clientName || resolvedOrgName || CLIENT.name

	// "Instructions by month" is always year to date, whatever the page's date
	// range is, so it has its own data set and its own request
	const ytd = useMemo(() => ytdRange(), [])
	const [ytdData, setYtdData] = useState([])
	const [ytdLoading, setYtdLoading] = useState(false)
	const [ytdError, setYtdError] = useState(false)
	const [ytdNonce, setYtdNonce] = useState(0)
	const [toastOpen, setToastOpen] = useState(false)

	const pickerNow = useMemo(() => new Date(), [])
	const pickerMin = useMemo(() => new Date(2025, 0, 1), [])

	useEffect(() => {
		if (roleProp != null) {
			setRole(roleProp)
			return
		}
		let alive = true
		profapi
			.getProfile()
			.then(r => alive && setRole(r && r.data ? r.data.role_id : 2))
			.catch(() => alive && setRole(2))
		return () => {
			alive = false
		}
	}, [roleProp])

	useEffect(() => {
		if (!showOrgFilter) return
		let alive = true
		org_api
			.getOrganisations()
			.then(r => {
				if (alive)
					setOrganisations(
						filterProdOrganisations((r.data && r.data.organisations) || []),
					)
			})
			.catch(() => alive && setOrganisations([]))
		return () => {
			alive = false
		}
	}, [showOrgFilter])

	useEffect(() => {
		if (clientName || role == null || showOrgFilter) return
		let alive = true
		let response
		if (role === ROLE_ADMIN && orgId) {
			response = org_api.getOrganisation(orgId)
		} else if (role === ROLE_ORGANISATION_ADMIN) {
			response = org_admin_api.getOrganisation()
		} else {
			response = customer_api.getOrganisation()
		}
		response
			.then(r => {
				const org = r.data && r.data.organisations && r.data.organisations[0]
				if (alive && org) setResolvedOrgName(org.name)
			})
			.catch(() => {})
		return () => {
			alive = false
		}
	}, [clientName, role, orgId, showOrgFilter])

	useEffect(() => {
		if (role == null) return
		const controller = new AbortController()
		setLoading(true)
		api
			.getReports({
				role,
				orgId,
				orgIds: showOrgFilter ? orgFilter : null,
				page: 1,
				page_size: 5000,
				dateRange: toDateRange(range.from, range.to),
				signal: controller.signal,
			})
			.then(res => {
				setData(res.report_details || [])
				setAgg(res.aggregates || null)
				setUpdatedAt(new Date())
				setLoading(false)
			})
			.catch(err => {
				// a newer filter/range change already aborted this request and
				// started its own fetch — leave its loading state alone
				if (isRequestCancelled(err)) return
				setLoading(false)
			})
		return () => controller.abort()
	}, [range, orgId, role, showOrgFilter, orgFilter])

	// second request, in parallel with the one above and independent of the
	// date range; a failure shows the chart's error state instead of mock data
	useEffect(() => {
		if (role == null) return
		const controller = new AbortController()
		setYtdLoading(true)
		setYtdError(false)
		setToastOpen(false)
		api
			.getReports({
				role,
				orgId,
				orgIds: showOrgFilter ? orgFilter : null,
				page: 1,
				page_size: 5000,
				dateRange: toDateRange(ytd.from, ytd.to),
				signal: controller.signal,
				throwOnError: true,
			})
			.then(res => {
				setYtdData(res.report_details || [])
				setYtdLoading(false)
			})
			.catch(err => {
				if (isRequestCancelled(err)) return
				setYtdData([])
				setYtdError(true)
				setToastOpen(true)
				setYtdLoading(false)
			})
		return () => controller.abort()
	}, [orgId, role, showOrgFilter, orgFilter, ytd, ytdNonce])

	useEffect(() => {
		let alive = true
		valuation_api
			.getCancellationReasons()
			.then(r => alive && setReasonGroups((r.data && r.data.groups) || []))
			.catch(() => alive && setReasonGroups([]))
		return () => {
			alive = false
		}
	}, [])

	const rows = useMemo(
		() => (range ? filterRange(data, range.from, range.to) : []),
		[data, range],
	)
	const ytdRows = useMemo(
		() => filterRange(ytdData, ytd.from, ytd.to),
		[ytdData, ytd],
	)

	const printDashboard = () => window.print()

	if (!range) {
		return (
			<div className='finvise-portal'>
				<div className='wrap' />
			</div>
		)
	}

	const m = metrics(rows)
	const status = caseStatusSummary(rows)
	const period = periodText(range)

	const turnHrs =
		agg && agg.instructionsAvgTime != null
			? agg.instructionsAvgTime
			: m.turn != null
			? m.turn * 24
			: null
	const pvqHrs = agg && agg.pvqAvgTime != null ? agg.pvqAvgTime : m.pvq
	const turn = fmtDuration(turnHrs)
	const pvq = fmtDuration(pvqHrs)


	const casesByRegion = (agg && agg.casesByRegion) || []

	// a single company gets its initials as the logo; the cross-company
	// admin view keeps the filter icon
	const logo = showOrgFilter ? null : initials(pageTitle)

	return (
		<div className='finvise-portal'>
			<div className='wrap'>
				<section className='page active fvd' id='page-dash'>
					{/* page head */}
					<div className='page-head'>
						<div className='title-row'>
							<div
								className={'brand-mark' + (logo ? ' initials' : '')}
								aria-hidden='true'
							>
								{logo || (
									<svg
										width='18'
										height='18'
										viewBox='0 0 24 24'
										fill='currentColor'
									>
										<path d='M3 5h18L13 15v6l-4-2v-4z' />
									</svg>
								)}
							</div>
							<div>
								<h1 id='client-name'>{pageTitle}</h1>
								<div className='sub'>
									<span className={'live-dot' + (loading ? ' loading' : '')} />
									<span>
										{'Last updated ' + (updatedAt ? fmtDate(updatedAt) : '')}
									</span>
									<span className='print-only'>
										{' · ' +
											range.label +
											' (' +
											fmtShort(range.from) +
											' – ' +
											fmtShort(range.to) +
											')'}
									</span>
								</div>
							</div>
						</div>
						<div className='head-actions'>
							{showOrgFilter ? (
								<FinviseOrgFilter
									organisations={organisations}
									selected={orgFilter}
									onChange={setOrgFilter}
								/>
							) : null}
							<FinviseRangePicker
								now={pickerNow}
								minDate={pickerMin}
								value={range}
								onChange={setRange}
							/>
							<button
								className='action-btn no-print'
								onClick={printDashboard}
								title='Download this dashboard as a PDF'
								style={{ display: 'none' }}
							>
								Download PDF
							</button>
						</div>
					</div>

					{/* KPI row */}
					<div className='grid-kpi'>
						<KpiCard
							label='Total RICS instructions'
							loading={loading}
							value={m.ricsApps.toLocaleString('en-GB')}
							sub={
								status.completed +
								' reports submitted · ' +
								status.inProgress +
								' in progress · ' +
								status.cancelled +
								' cancelled'
							}
						/>
						<KpiCard
							label='Instruction to report - avg time'
							loading={loading}
							value={turn ? turn.value : '—'}
							unit={turn ? turn.unit : null}
							sub='From instruction to report received, including valuer and underwriter queries'
						/>
						<KpiCard
							label='Average fee per report'
							disabled
							value='—'
							sub={'Total fees — ex VAT, ' + period}
							note='*Data available from 1 Sep 2026'
						/>
						<KpiCard
							label='PVQ response - avg time'
							loading={loading}
							value={pvq ? pvq.value : '—'}
							unit={pvq ? pvq.unit : null}
							sub='From client enquiry to valuer response'
						/>
					</div>

					<CaseStatusCard summary={status} loading={loading} />

					<CancelledCard
						data={buildCancellations(reasonGroups, rows)}
						periodText={period}
					/>

					<div className='fvd-two'>
						<MonthsCard
							rows={ytdRows}
							from={ytd.from}
							to={ytd.to}
							loading={ytdLoading}
							error={ytdError}
							onRetry={() => setYtdNonce(n => n + 1)}
						/>
						<GeographyCard
							regions={casesByRegion}
							loading={loading}
						/>
					</div>
				</section>
			</div>

			<LoadErrorToast
				open={toastOpen}
				onRefresh={() => setYtdNonce(n => n + 1)}
				onDismiss={() => setToastOpen(false)}
			/>
		</div>
	)
}

export default FinviseDashboard
